import { Inject, Injectable, NgZone } from "@angular/core";
import { tap } from "rxjs/operators";
import {
  bufferTime,
  distinctUntilChanged,
  filter,
  finalize,
  from,
  fromEvent,
  interval,
  map,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
  timer
} from "rxjs";

import { TAutoLogoutConfig } from "@app/core/auto-logout/auto-logout.type";
import { AUTO_LOGOUT_CONFIG } from "@app/core/auto-logout/auto-logout.injection";

@Injectable({ providedIn: "root" })
export class AutoLogoutService {
  private theActivityEvents$!: Observable<unknown>;
  private theActivityTimer$!: Observable<unknown>;
  private theTimer$!: Observable<number>;

  private theTimerStart$ = new Subject<boolean>();
  private theTimeout$ = new Subject<boolean>();

  private isInactivityTimer = false;
  private isTimerDetected = false;
  private isTimeout = false;

  private theTimerSubscription!: Subscription;

  constructor(
    private readonly ngZone: NgZone,
    @Inject(AUTO_LOGOUT_CONFIG) private readonly config: TAutoLogoutConfig
  ) {}

  doStartWatching() {
    if (!this.theActivityEvents$) {
      this.theActivityEvents$ = merge(
        fromEvent(window, 'mousemove'),
        fromEvent(window, 'resize'),
        fromEvent(document, 'keydown')
      );
    }

    this.theActivityTimer$ = from(this.theActivityEvents$);

    if (this.theTimerSubscription) {
      this.theTimerSubscription.unsubscribe();
    }

    this.theTimerSubscription = this.theActivityTimer$
      .pipe(
        bufferTime(1000),
        filter((timeSpan) => !timeSpan.length && !this.isTimerDetected && !this.isInactivityTimer),
        tap(() => this.isTimerDetected = true),
        switchMap(() =>
          this.ngZone.runOutsideAngular(() =>
            interval(1000).pipe(
              takeUntil(
                merge(
                  this.theActivityEvents$,
                  timer(this.config.timeActivityMs).pipe(
                    tap(() => {
                      this.isInactivityTimer = true;
                      this.theTimerStart$.next(true);
                    })
                  )
                )
              ),
              finalize(() => this.isTimerDetected = false)
            )
          )
        ),
      ).subscribe();

    this.doSetupTimer(this.config.timeShowNotificationSec);
  }

  doStopWatching(): void {
    this.stopTimer();

    if (this.theTimerSubscription) {
      this.theTimerSubscription.unsubscribe();
    }
  }

  resetTimer(): void {
    this.stopTimer();

    this.isTimeout = false;
  }

  doTimerStart(): Observable<number | null> {
    return this.theTimerStart$.pipe(
      distinctUntilChanged(),
      switchMap((start) =>
        (start ? this.theTimer$ : of(null))
      )
    );
  }

  doTimeOutEvent(): Observable<boolean> {
    return this.theTimeout$.pipe(
      filter((timeout) => timeout),
      tap(() => (this.isTimeout = true)),
      map(() => true)
    );
  }

  private doSetupTimer(timeout: number): void {
    this.ngZone.runOutsideAngular(() => {
      this.theTimer$ = of(() => new Date()).pipe(
        map((fn) => fn()),
        switchMap((startDate) =>
          interval(1000).pipe(
            map(() => Math.round((new Date().valueOf() - startDate.valueOf()) / 1000)),
            tap((elapsed) => {
              if (elapsed >= timeout) {
                this.theTimeout$.next(true);
              }
            })
          )
        )
      );
    });
  }

  private stopTimer() {
    this.isInactivityTimer = false;

    this.theTimerStart$.next(false);
  }
}
